import * as Sentry from "@sentry/react";
import { useState, useEffect } from "react";
import Dropzone from "react-dropzone";
import { useProjectStore } from "../../stores/projectStore";
import Auth, { isExpectedApiResponseError } from "../../Auth";
import { useAppStore } from "../../stores/appStore";
import classNames from "classnames";
import InlineSVG from "react-inlinesvg";
import { Container } from "./styles";

export function FileImportButton({ setImportSessionId, disable, reset, setUploading, uploadName, setUploadName, onImportSuccess, onImportError }) {
  const project = useProjectStore((state) => state.project);
  const user = useAppStore((state) => state.user);

  const [loading, setLoading] = useState(false);

  const importDocument = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("projectId", project.id);
      const result = await Auth.postFilesWithIdToken("/api/convert", { user, projectId: project.id }, formData);
      const { success, sessionId } = result;

      // The file data has been stored in Redis, and we can validate it
      if (success && sessionId) {
        if (setImportSessionId) {
          setImportSessionId(sessionId);
        }
        if (onImportSuccess) {
          onImportSuccess({ sessionId, parsed: result.parsed, text: result.text });
        }
      } else {
        if (onImportError) {
          onImportError(result.error || "We were unable to import your file. Please check your formatting and try again.");
        }
      }
    } catch (err) {
      if (!isExpectedApiResponseError(err)) {
        Sentry.captureException(err, { tags: { projectId: project.id, feature: "file_import" } });
      }
      if (onImportError) {
        onImportError(err?.message || "We were unable to import your file. Please check your formatting and try again.");
      }
    }
  };

  const disabled = loading || disable; // disable dropzone if we are using pasted text workflow

  useEffect(() => {
    if (loading) setUploading(loading);
    // we don't want to reset uploading once loading is complete
    // this is used to turn off the ability to paste in text, which we don't want to do if we are uploading a file
  }, [loading, setUploading]);

  return (
    <Container className={classNames({ disable })}>
      <Dropzone
        aria-label="import"
        accept={{
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx", ".doc"],
          "text/plain": [".txt"],
          "application/rtf": [".rtf"],
          "application/vnd.oasis.opendocument.text": [".odt"],
          "text/csv": [".csv"],
        }}
        disabled={disabled}
        onDrop={async (acceptedFiles) => {
          const acceptedFile = acceptedFiles?.[0];
          if (!acceptedFile) return;

          setLoading(true);
          try {
            await importDocument(acceptedFile);
            setUploadName(acceptedFile.name);
          } finally {
            setLoading(false);
          }
        }}
      >
        {({ getRootProps, getInputProps, isDragReject, isDragAccept }) => (
          <div
            {...getRootProps()}
            className={classNames({ upload: true, rejected: isDragReject, accepted: isDragAccept, success: !!uploadName, loading, disabled })}
          >
            <input {...getInputProps()} />
            {!uploadName && !loading && (
              <>
                <InlineSVG
                  src="/assets/import.svg"
                  className="import-icon"
                />
                <p className="title">Drag your File here</p>
                <p className="supported-format">(.txt, .doc, .docx, .rtf, .odt, .csv)</p>
                <p className="upload-link">
                  or <a>click here to browse</a>
                </p>
              </>
            )}
            {loading && <p>Uploading your file...</p>}
            {uploadName && !loading && <p className="upload-name">Uploaded file: {uploadName}</p>}
          </div>
        )}
      </Dropzone>
    </Container>
  );
}
